import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Sparkles, ExternalLink, Copy, Check } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCENT = '#2563EB';
const COUPON = '50OFF';

export const AdSniperModal: React.FC<Props> = ({ open, onOpenChange }) => {
  useLanguage();
  const [copied, setCopied] = useState(false);

  const handleVisit = () => {
    window.open('https://www.adsniper.com.br/', '_blank', 'noopener,noreferrer');
    onOpenChange(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(COUPON);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="p-0 gap-0 border-[#E4E7EC] w-[min(94vw,520px)] max-w-none rounded-[16px] bg-white flex flex-col max-h-[90vh] overflow-hidden"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div
              className="text-[11px] font-semibold uppercase tracking-[0.08em] mb-2"
              style={{ color: '#9CA3AF' }}
            >
              {translateInterfaceText("Ferramentas · AdSniper")}</div>
            <h2 className="text-[18px] font-semibold text-[#0F172A] leading-tight">
              {translateInterfaceText("Descubra os criativos e ofertas mais escaladas no META")}</h2>
            <p className="text-[11.5px] text-[#6B7280] mt-1.5 leading-[1.5]">
              {translateInterfaceText("Conheça a AdSniper com uma condição especial para usuários da Host VSL.")}</p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="w-9 h-9 rounded-full flex items-center justify-center text-[#6B7280] bg-white border border-[#E4E7EC] hover:bg-[#F3F4F6] hover:text-[#0F172A] transition-all flex-shrink-0"
            aria-label={translateInterfaceText("Fechar")}
          >
            <X className="w-4 h-4" strokeWidth={2.25} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-2 flex-1 overflow-y-auto min-h-0">
          {/* Offer Card */}
          <div
            className="rounded-[14px] px-6 py-7 mb-4 relative overflow-hidden"
            style={{
              background: '#EAF1FF',
              border: '1px solid #DCE6FF',
            }}
          >
            <div className="flex items-center gap-2 mb-4" style={{ color: ACCENT }}>
              <Sparkles className="w-3.5 h-3.5" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">
                {translateInterfaceText("Oferta exclusiva")}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-[36px] font-bold leading-none tracking-tight" style={{ color: ACCENT }}>{translateInterfaceText("50% OFF")}</span>
              <span className="text-[15px] font-medium text-[#0F172A]">{translateInterfaceText("em todos os planos")}</span>
            </div>
            <p className="text-[12.5px] text-[#4B5563] leading-[1.5]">
              {translateInterfaceText("Use o cupom abaixo no checkout do plano que você escolher.")}</p>
          </div>

          {/* Coupon Block */}
          <div
            className="rounded-[14px] border border-dashed p-4 mb-2"
            style={{ borderColor: '#DCE6FF', background: '#F8FAFF' }}
          >
            <div
              className="text-[10.5px] font-semibold uppercase tracking-[0.08em] mb-2.5"
              style={{ color: '#6B7280' }}
            >
              {translateInterfaceText("Cupom de desconto")}</div>
            <div className="flex items-center gap-2.5 mb-2.5">
              <div
                className="flex-1 rounded-[10px] bg-white border border-[#E0E7FF] px-4 py-3 font-mono text-[20px] font-bold tracking-[0.12em] text-center"
                style={{ color: ACCENT }}
              >
                {COUPON}
              </div>
              <button
                onClick={handleCopy}
                className="h-[50px] px-4 rounded-[10px] font-medium text-[13px] flex items-center gap-1.5 transition-all bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] border border-[#E4E7EC]"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" style={{ color: ACCENT }} />
                    {translateInterfaceText("Copiado")}</>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    {translateInterfaceText("Copiar cupom")}</>
                )}
              </button>
            </div>
            <p className="text-[12px] text-[#6B7280] leading-[1.5]">
              {translateInterfaceText("Adicione este cupom no checkout do plano que você gostar.")}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#EEF0F3] bg-[#FCFCFD] flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#6B7280] hover:bg-transparent hover:text-[#6B7280]"
          >
            {translateInterfaceText("Agora não")}</Button>
          <Button
            onClick={handleVisit}
            style={{ background: '#1D4ED8', color: '#fff' }}
            className="hover:brightness-110 font-semibold px-5"
          >
            {translateInterfaceText("VER SITE")}<ExternalLink className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
